import { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { BaggageWarning } from 'pages/avia/detail-ticket/components/baggage-warning'
import { getBaggageWarning } from 'pages/avia/detail-ticket/libs/getBaggageWarning.tsx'
import { InsuranceDetailSource } from 'pages/avia/insurance-services/model/model.ts'

import { favoritesStore } from 'widgets/avia/favorites'

import { getCountOfPassengers } from 'entities/favorite/lib/count-of-passangers.ts'
import { getSearchForm } from 'entities/favorite/lib/get-search-form'
import { isDifficultAviaRoute } from 'entities/ticket/lib/is-difficult-avia-route'
import findTicketFormStore from 'entities/ticket/models/find-ticket/find-ticket-store'
import formPassengersStore from 'entities/ticket/models/form-passengers/form-passengers-store'
import listTicketsStore from 'entities/ticket/models/list-tickets/list-tickets-store'
import TarrifPop from 'entities/ticket/ui/tarrif-pop'
import { TicketDirectionDetails } from 'entities/ticket/ui/ticket-direction-details'

import { FavoriteParams } from 'shared/api/avia/favorites'
import ErrorImage from 'shared/images/error.png'
import { checkUserAuth } from 'shared/lib/checkUserAuth'
import { getErrorMessage } from 'shared/lib/errorText'
import { prepareDetailTicketInfo } from 'shared/lib/ticketRender'
import serialDocumentStore from 'shared/store/serial-document-store'
import { AppAdviceText } from 'shared/ui/app-advice-text'
import { CharterButton } from 'shared/ui/charter-button'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { FlightDetailsPopup } from 'shared/ui/flight-details-poup'
import { Loader } from 'shared/ui/loader'
import { PopupContent } from 'shared/ui/poup-content'

import { Header } from '../detail-ticket/components/header'
import {
  SelectTicket,
  TicketTariff,
} from '../detail-ticket/components/ticket-tariff'

import styles from './styles.module.scss'

const DetailTicket = observer(() => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { openDialog, closeDialog } = useDialogBottomSheet()

  const { favoriteId } = useParams()
  const location = useLocation()

  const isFavoriteLoading = favoritesStore.detailLoading

  const abortControllerRef = useRef<AbortController | null>(null)

  const searchParamsFavorite: FavoriteParams = location.state

  const onLoadFavoriteDetail = async () => {
    try {
      if (favoriteId === favoritesStore.favoritesDetail?.favorite_search_key)
        return

      abortControllerRef.current = new AbortController()

      await favoritesStore.fetchFavoritesDetail({
        id: favoriteId!,
        searchParams: searchParamsFavorite!,
        abortController: abortControllerRef.current!,
      })

      if (!serialDocumentStore?.info?.length)
        await serialDocumentStore.getSerialDocumentInfo()
    } catch (err) {
      const errorText = getErrorMessage(err)

      openDialog({
        child: (
          <PopupContent
            image={ErrorImage}
            title={t('error')}
            button={t('try_again')}
            description={errorText || t('please_tey_again')}
            onClick={() => {
              navigate(RoutingPaths.avia.favorites.path())
              closeDialog()
            }}
          />
        ),
        isDialog: false,
        isClickable: false,
      })
    }
  }

  useEffect(() => {
    if (favoriteId && searchParamsFavorite) {
      void onLoadFavoriteDetail()
    } else {
      navigate(RoutingPaths.avia.favorites.path(), { replace: true })
    }

    return () => abortControllerRef.current?.abort()
  }, [])

  const onBack = () => {
    abortControllerRef.current?.abort()

    if (window.history.length > 1) {
      navigate(-1)
      return
    }

    navigate(RoutingPaths.avia.favorites.path())
  }

  const onBuyTicket = async (ticketGroup: SelectTicket) => {
    if (!ticket) return

    await checkUserAuth({
      onSuccess: () => {
        formPassengersStore.resetTicketInsurances()
        listTicketsStore.setSelectedTicket(ticketGroup)

        findTicketFormStore.setFormFindTicket(
          getSearchForm(ticketGroup.ticket, searchParamsFavorite!),
        )

        navigate(RoutingPaths.avia.insuranceService.path(), {
          state: { detailSource: InsuranceDetailSource.favorite },
        })
      },
    })
  }

  const ticket = favoritesStore.favoritesDetail
  const formSearchParams = getSearchForm(ticket, searchParamsFavorite)

  const favoritesParams = {
    params: searchParamsFavorite,
    directions: formSearchParams?.directions,
  }
  const detailTicket = ticket ? prepareDetailTicketInfo(ticket) : null
  const warningText = getBaggageWarning(detailTicket?.detailTicketInfo)
  const isDifficultRoute = isDifficultAviaRoute(formSearchParams?.directions)

  const onDetailBaggage = (indexes: number[]) => {
    if (!detailTicket) return

    const [directionNum, segmentNum] = indexes
    const segment =
      detailTicket?.resultsAllListDirections?.[directionNum]?.[segmentNum]

    openDialog({
      child: (
        <FlightDetailsPopup
          flightCode={`${t('flight')} ${segment?.carrier.code}-${segment?.flight_number}`}
          timeFlight={`${segment?.dep?.time || ''}`}
          airplane={segment?.aircraft?.title || ''}
          baggage={segment?.baggage}
          cBaggage={segment?.cbaggage}
        />
      ),
    })
  }

  const onShowTariff = (selectedTicket: SelectTicket['ticket']) => {
    if (!selectedTicket) return

    openDialog({
      child: (
        <TarrifPop
          key={selectedTicket.tid}
          rulesParams={selectedTicket}
          additionalTariff={
            selectedTicket?.bookit_fare_data?.fares?.description
          }
        />
      ),
    })
  }

  const countOfPassengers = getCountOfPassengers(searchParamsFavorite)
  return (
    <div className={styles.container}>
      {isFavoriteLoading ? (
        <div className={styles.loader}>
          <Loader />

          <AppAdviceText />
        </div>
      ) : (
        ticket && (
          <>
            <div className={styles.header}>
              <Header
                onBack={onBack}
                ticket={ticket}
                favoritesParams={favoritesParams}
                totalCountPassengers={countOfPassengers}
              />
            </div>

            {ticket.is_charter && (
              <div className={styles.detail}>
                <CharterButton />
              </div>
            )}

            {detailTicket?.detailTicketInfo &&
              detailTicket?.resultsAllListDirections && (
                <TicketDirectionDetails
                  detailTicketInfo={detailTicket.detailTicketInfo}
                  resultsAllListDirections={
                    detailTicket.resultsAllListDirections
                  }
                  isDifficultRoute={isDifficultRoute}
                  getDirectionKey={(group) => group.title}
                  onDetail={(index, flyIndex) =>
                    onDetailBaggage([index, flyIndex])
                  }
                />
              )}

            {warningText && <BaggageWarning title={warningText} />}

            <TicketTariff
              passengerCount={countOfPassengers}
              ticket={ticket}
              onSelectTicket={onBuyTicket}
              onShowTariffInfo={onShowTariff}
            />
          </>
        )
      )}
    </div>
  )
})

export default DetailTicket
